/*
 * A company's footer: what SiteFooter.tsx renders for a deployment whose
 * SITE_FOOTER_SCOPE names one. The markup is one, and each company's content is
 * data in companyFooters.ts - so the accoTOOL and BIaaS footers cannot drift
 * apart in anything but what they say.
 *
 * The layout mirrors the companies' own sites: the logo, a strapline and
 * LinkedIn on the left, three columns of links on the right - Product, Resources,
 * Contact.
 *
 * THE COLUMN TITLES ARE NOT HEADINGS. Each column is a nav labelled by its title,
 * which is what a screen reader needs to find "Contact"; making the titles h2s
 * would put three footer entries into every page's heading outline, which the
 * pages keep deliberate (App.test.tsx pins the browse page's).
 *
 * The links open in the same tab, as links between pages of one company's sites
 * do; LinkedIn opens a new one, as the companies' own footers have it.
 *
 * LANGUAGE (S48, 2026-10-29). The column titles and LinkedIn's name follow the
 * chosen language - "PRODUKT", "RESSOURCER", "KONTAKT" in Danish - so a column
 * names its title by key, and the dictionary says it. The links, the strapline
 * and the logo's name are the company's own words, as they stand on its site,
 * and stay as written.
 */

import { useMessages } from '../i18n/language.js';

export interface FooterColumn {
  /** Which title the column carries; the words are the dictionary's. */
  title: 'product' | 'resources' | 'contact';
  links: ReadonlyArray<{ label: string; href: string }>;
}

export interface CompanyFooterContent {
  /** The company's own site, which the logo links to. */
  site: string;
  /**
   * A bundled, white-only logo: the stylesheet darkens it on the light side
   * (.site-footer-logo). `alt` is also the logo link's name, and the company's
   * name in LinkedIn's ("accoTOOL on LinkedIn").
   */
  logo: { src: string; alt: string; width: number; height: number };
  strapline: string;
  linkedIn: { href: string };
  columns: readonly FooterColumn[];
}

export function CompanyFooter({ content }: { content: CompanyFooterContent }): JSX.Element {
  const t = useMessages();

  return (
    <footer className="site-footer site-footer-rich">
      <div className="page site-footer-grid">
        <div className="site-footer-about">
          <a className="site-footer-logo" href={content.site}>
            <img
              src={content.logo.src}
              alt={content.logo.alt}
              width={content.logo.width}
              height={content.logo.height}
            />
          </a>
          <p className="site-footer-strapline">{content.strapline}</p>
          <a
            className="site-footer-social"
            href={content.linkedIn.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.footer.linkedIn(content.logo.alt)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zM9 9h3.8v1.7h.06c.53-1 1.83-2.05 3.77-2.05C20.2 8.65 21 11 21 14.1V21h-4v-6.1c0-1.46-.03-3.34-2.04-3.34-2.04 0-2.35 1.6-2.35 3.24V21H9z" />
            </svg>
          </a>
        </div>

        {content.columns.map((column) => {
          const titleId = `site-footer-${column.title}`;
          return (
            <nav key={column.title} className="site-footer-column" aria-labelledby={titleId}>
              <p className="site-footer-column-title" id={titleId}>
                {t.footer.columns[column.title]}
              </p>
              <ul>
                {column.links.map((link) => (
                  <li key={link.href}>
                    <a href={link.href}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </nav>
          );
        })}
      </div>
    </footer>
  );
}
